/**
 * Перенос символов между биржами (кроме Rithmic).
 *
 * Биржи именуют один и тот же инструмент по-разному:
 *   Bybit / Bitget / Binance — LITUSDT
 *   OKX            — LIT-USDT-SWAP  (бессрочный контракт; формат обязателен API)
 *
 * Чтобы «скопировать» символ с одной биржи и «импортировать» на другую одной
 * кнопкой, приводим имя к канону (base + quote) и пересобираем под целевую
 * биржу.  Конфиг символа переносим в биржа-независимом (camelCase) виде;
 * инструментные поля (priceStep/quantityStep/ctVal/digits) НЕ копируем — форма
 * целевой биржи пересчитает их сама из выбранного символа.
 */

export type ExchangeKey = 'bybit' | 'bitget' | 'binance' | 'okx'

/** Канонический (биржа-независимый) конфиг символа для переноса. */
export interface CanonicalSymbolConfig {
    name?: string
    stepSize?: number
    defaultPositionSize?: number
    defaultMaxLots?: number
    minBalance?: number
    takeProfitRatio?: number
    feedCount?: number
    onlyAdmin?: boolean
    hedgingEnable?: boolean
    candleType?: string
    candleVolume?: number
    interval?: string
    hedgeV2Enable?: boolean
    hedgeV2UseLevels?: boolean
}

/** Полезная нагрузка в буфере обмена. */
export interface SymbolClipboardPayload {
    __rcSymbol: true
    source: ExchangeKey
    base: string
    quote: string
    rawSymbol: string
    config: CanonicalSymbolConfig
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyRecord = Record<string, any>

// Известные котируемые валюты — для разбора «склеенного» символа (LITUSDT →
// LIT + USDT).  Порядок важен: сначала длинные/частые, USD после USDT/USDC.
const QUOTES = ['USDT', 'USDC', 'BUSD', 'TUSD', 'FDUSD', 'DAI', 'USD', 'BTC', 'ETH', 'EUR']

/** Разбор биржевого символа в { base, quote }. */
export const parseSymbol = (
    symbol: string,
    exchange: ExchangeKey
): { base: string; quote: string } => {
    const s = (symbol || '').toUpperCase()
    if (exchange === 'okx') {
        // BTC-USDT-SWAP → BTC, USDT
        const [base, quote] = s.split('-')
        return { base: base || s, quote: quote || 'USDT' }
    }
    // bybit / binance: BTCUSDT
    const quote = QUOTES.find(q => s.endsWith(q)) || 'USDT'
    return { base: s.slice(0, s.length - quote.length) || s, quote }
}

/** Сборка биржевого символа целевой биржи из { base, quote }. */
export const buildSymbol = (
    base: string,
    quote: string,
    exchange: ExchangeKey
): string => {
    const b = (base || '').toUpperCase()
    const q = (quote || 'USDT').toUpperCase()
    return exchange === 'okx' ? `${b}-${q}-SWAP` : `${b}${q}`
}

/** Запись символа биржи → канонический конфиг. */
export const toCanonicalConfig = (
    record: AnyRecord,
    exchange: ExchangeKey
): CanonicalSymbolConfig => {
    if (exchange === 'binance') {
        // Binance хранит поля в snake_case; per-symbol feedCount отсутствует.
        return {
            name: record.name,
            stepSize: record.step_size,
            defaultPositionSize: record.default_position_size,
            defaultMaxLots: record.default_max_lots,
            minBalance: record.min_balance,
            takeProfitRatio: record.take_profit_ratio,
            onlyAdmin: record.only_admin,
            hedgingEnable: record.hedging_enable,
            candleType: record.candle_type,
            candleVolume: record.candle_volume,
            interval: record.interval,
            hedgeV2Enable: record.hedge_v2_enable,
            hedgeV2UseLevels: record.hedge_v2_use_levels,
        }
    }
    // bybit / okx — поля уже в camelCase
    return {
        name: record.name,
        stepSize: record.stepSize,
        defaultPositionSize: record.defaultPositionSize,
        defaultMaxLots: record.defaultMaxLots,
        minBalance: record.minBalance,
        takeProfitRatio: record.takeProfitRatio,
        feedCount: record.feedCount,
        onlyAdmin: record.onlyAdmin,
        hedgingEnable: record.hedgingEnable,
        candleType: record.candleType,
        candleVolume: record.candleVolume,
        interval: record.interval,
        hedgeV2Enable: record.hedgeV2Enable,
        hedgeV2UseLevels: record.hedgeV2UseLevels,
    }
}

/** Канонический конфиг → initialValues формы целевой биржи (undefined чистим,
 *  чтобы не перетереть дефолты формы). */
export const fromCanonicalConfig = (
    config: CanonicalSymbolConfig,
    exchange: ExchangeKey
): AnyRecord => {
    const c = config || {}
    const out: AnyRecord =
        exchange === 'binance'
            ? {
                  name: c.name,
                  step_size: c.stepSize,
                  default_position_size: c.defaultPositionSize,
                  default_max_lots: c.defaultMaxLots,
                  min_balance: c.minBalance,
                  take_profit_ratio: c.takeProfitRatio,
                  only_admin: c.onlyAdmin,
                  hedging_enable: c.hedgingEnable,
                  candle_type: c.candleType,
                  candle_volume: c.candleVolume,
                  interval: c.interval,
                  hedge_v2_enable: c.hedgeV2Enable,
                  hedge_v2_use_levels: c.hedgeV2UseLevels,
              }
            : {
                  name: c.name,
                  stepSize: c.stepSize,
                  defaultPositionSize: c.defaultPositionSize,
                  defaultMaxLots: c.defaultMaxLots,
                  minBalance: c.minBalance,
                  takeProfitRatio: c.takeProfitRatio,
                  feedCount: c.feedCount,
                  onlyAdmin: c.onlyAdmin,
                  hedgingEnable: c.hedgingEnable,
                  candleType: c.candleType,
                  candleVolume: c.candleVolume,
                  interval: c.interval,
                  hedgeV2Enable: c.hedgeV2Enable,
                  hedgeV2UseLevels: c.hedgeV2UseLevels,
              }
    Object.keys(out).forEach(k => out[k] === undefined && delete out[k])
    return out
}

/** Собрать payload из строки таблицы символов. */
export const buildSymbolPayload = (
    record: AnyRecord,
    exchange: ExchangeKey
): SymbolClipboardPayload => {
    const { base, quote } = parseSymbol(record.symbol, exchange)
    return {
        __rcSymbol: true,
        source: exchange,
        base,
        quote,
        rawSymbol: record.symbol,
        config: toCanonicalConfig(record, exchange),
    }
}

/** Записать символ в системный буфер обмена. */
export const writeSymbolPayload = async (
    payload: SymbolClipboardPayload
): Promise<void> => {
    await navigator.clipboard.writeText(JSON.stringify(payload))
}

/** Прочитать символ из буфера (null, если там не наш payload). */
export const readSymbolPayload =
    async (): Promise<SymbolClipboardPayload | null> => {
        try {
            const text = await navigator.clipboard.readText()
            const data = JSON.parse(text)
            return data && data.__rcSymbol === true
                ? (data as SymbolClipboardPayload)
                : null
        } catch {
            return null
        }
    }
